import SEO from "@/components/SEO";
import { canonical } from "@/utils/seo";
import { useEffect, useRef } from "react";

export default function DanshuiMarketEvent() {
  const feedbackFrameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data && e.data.type === "feedback-resize") {
        const frame = feedbackFrameRef.current;
        if (frame) {
          frame.style.height = `${e.data.height + 20}px`;
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        meta={{
          title: "2026淡江大學學生會春日拾光文創市集｜美好時光指南",
          description:
            "2026年5月18日至22日，淡江大學學生會舉辦春日拾光文創市集，美好時光指南帶來頭撥減壓、修眉與心理色彩能量檢測體驗，邀你一起探索校園文創新風潮。",
          canonical: canonical("/events/danshui-market-2026"),
          keywords: [
            "淡江大學",
            "春日拾光文創市集",
            "學生會",
            "美好時光指南",
            "校園市集",
            "文創市集",
          ],
          ogType: "article",
        }}
      />

      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20vibrant%20outdoor%20university%20campus%20cultural%20and%20creative%20market%20fair%20in%20Taiwan%20during%20spring%20afternoon%2C%20featuring%20colorful%20student-run%20stalls%20with%20handmade%20crafts%20and%20beauty%20products%20under%20white%20canopy%20tents%20on%20a%20tree-lined%20campus%20pathway%2C%20young%20university%20students%20browsing%20and%20socializing%2C%20cherry%20blossom%20trees%20in%20soft%20bloom%2C%20warm%20golden%20sunlight%20filtering%20through%20leaves%2C%20a%20mix%20of%20academic%20buildings%20and%20green%20lawns%20in%20soft%20bokeh%20background%2C%20lively%20youthful%20atmosphere%20with%20warm%20orange%20and%20cream%20color%20accents%2C%20lifestyle%20event%20photography%20with%20cheerful%20spring%20vibes%2C%20no%20text%20no%20logos%2C%20no%20blue%20or%20purple%20dominant%20colors&width=1440&height=600&seq=tamkang-market-hero-v2&orientation=landscape"
          alt="淡江大學春日拾光文創市集主視覺"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-full mb-4 tracking-wide">
            2026.5/18 — 5/22
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 tracking-tight">
            美好時光X淡江大學春日拾光文創市集
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-xl">
            頭撥減壓 · 修眉 · 色彩能量檢測
          </p>
        </div>
      </section>

      {/* Event Info */}
      <section className="w-full px-4 md:px-8 lg:px-14 py-10 md:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Left: Info Cards */}
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">
                  2026淡江大學學生會春日拾光文創市集
                </h2>
                <p className="text-slate-500 text-sm">
                  淡江大學學生會 × 美好時光指南
                </p>
                <div className="mt-4 space-y-3 text-slate-600 text-sm leading-relaxed">
                  <p className="text-lg font-bold text-slate-800 text-center">伶活美學</p>
                  <p>
                    我們是「伶活美學」，像靈魂的光線，為大家帶來靈活的啟發與無限的可能。
                  </p>
                  <p className="font-semibold text-slate-800 text-center">
                    「每個人都擁有屬於自己的命定！」
                  </p>
                  <p>
                    在這裡，我們提供色彩分析、修眉服務、塔羅牌和占星諮詢，幫助你找到最適合的顏色與人生方向，讓靈魂與生活更加和諧。
                  </p>
                  <p>
                    我們相信，美不分性別，不只是女生需要了解適合自己的顏色，男生也能透過色彩找到專屬於自己的穿搭風格與自信，穿出最適合自己的樣子。
                  </p>
                  <p>
                    此外，我們還販售各式舒壓小物與實用商品，讓你放鬆身心，提升生活品質。
                  </p>
                  <p className="font-medium text-slate-700">
                    歡迎來伶活美學，讓我們的光照亮你的心靈，帶領你走向更美好的未來，釋放無限潛能！
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-stone-50 rounded-xl p-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-100 text-orange-700 mb-3">
                    <i className="ri-map-pin-line text-lg" />
                  </div>
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">
                    活動地點
                  </p>
                  <p className="text-sm font-semibold text-slate-800">
                    淡江大學海報街文創市集
                  </p>
                  <p className="text-xs text-slate-500 mt-1">校園內步行即可抵達</p>
                </div>
              </div>

              <div className="bg-white border border-slate-100 rounded-xl p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">
                  以下是服務項目
                </h3>
                <div className="space-y-4">
                  {/* 頭撥減壓 */}
                  <div className="flex items-start gap-3 bg-orange-50/60 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-100 text-orange-700 shrink-0">
                      <i className="ri-heart-pulse-line text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 mb-1">
                        頭撥減壓
                      </p>
                      <p className="text-xs text-slate-500 mb-2">
                        運用撥筋手法舒緩頭部緊繃，釋放壓力、改善循環，短短幾分鐘就能感覺神清氣爽。
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-md">
                          3分鐘免費
                        </span>
                        <span className="text-[11px] text-slate-400">/</span>
                        <span className="inline-block px-2 py-0.5 bg-white text-slate-600 text-[11px] font-medium rounded-md border border-slate-100">
                          10分鐘 150元
                        </span>
                        <span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 text-[11px] font-semibold rounded-md">
                          贈按摩梳
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 修眉 */}
                  <div className="flex items-start gap-3 bg-orange-50/60 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-100 text-orange-700 shrink-0">
                      <i className="ri-brush-line text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 mb-1">
                        修眉
                      </p>
                      <p className="text-xs text-slate-500 mb-2">
                        專業眉型設計，依照臉型與五官比例打造最適合你的眉型，讓整體氣色瞬間提升。
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-md">
                          前20名免費
                        </span>
                        <span className="text-[11px] text-slate-400">/</span>
                        <span className="inline-block px-2 py-0.5 bg-white text-slate-600 text-[11px] font-medium rounded-md border border-slate-100">
                          後續 150元
                        </span>
                        <span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 text-[11px] font-semibold rounded-md">
                          贈簡易色彩測色
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 心理色彩能量檢測 */}
                  <div className="flex items-start gap-3 bg-orange-50/60 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-100 text-orange-700 shrink-0">
                      <i className="ri-palette-line text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 mb-1">
                        心理色彩能量檢測
                      </p>
                      <p className="text-xs text-slate-500 mb-2">
                        透過色彩心理學快速解析你的能量狀態，找出最適合你的用色方向，從穿搭到妝容都有依據。
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-block px-2 py-0.5 bg-white text-slate-600 text-[11px] font-medium rounded-md border border-slate-100">
                          一次 150元 / 15分鐘
                        </span>
                        <span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 text-[11px] font-semibold rounded-md">
                          贈好氣色補妝
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Image + Description */}
            <div className="space-y-6">
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20vibrant%20outdoor%20university%20campus%20cultural%20creative%20market%20fair%20in%20Taiwan%2C%20featuring%20colorful%20handmade%20craft%20stalls%20and%20beauty%20pop-up%20booths%20under%20white%20canopy%20tents%20on%20a%20tree-lined%20campus%20walkway%2C%20students%20browsing%20handmade%20accessories%20and%20skincare%20products%2C%20cherry%20blossom%20trees%20and%20green%20lawns%20in%20the%20background%2C%20warm%20spring%20afternoon%20sunlight%2C%20cheerful%20youthful%20atmosphere%20with%20soft%20pastel%20tones%20of%20cream%20peach%20and%20sage%20green%2C%20lifestyle%20event%20photography%2C%20no%20text%20no%20logos%2C%20inviting%20campus%20community%20vibe&width=800&height=600&seq=tamkang-market-detail-v2&orientation=landscape"
                  alt="淡江大學春日拾光文創市集攤位示意"
                  className="w-full h-full object-cover object-center"
                />
              </div>

              {/* LINE 社群 */}
              <a
                href="https://line.me/ti/g2/EixNmQRnMAW2-f1y102n53NaGECfYGZhtpJMiw?utm_source=invitation&utm_medium=link_copy&utm_campaign=default"
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-[#06C755] rounded-xl p-5 text-white hover:bg-[#05a347] transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 shrink-0">
                    <i className="ri-chat-3-line text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold mb-0.5">美好時光 LINE 社群</p>
                    <p className="text-xs text-white/80">
                      加入社群掌握最新市集動態與獨家優惠
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-white/70 group-hover:text-white transition-colors shrink-0" />
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Form Embed Section */}
      <section className="w-full px-4 md:px-8 lg:px-14 py-10 md:py-16 bg-stone-50">
        <div className="max-w-[800px] mx-auto">
          <iframe
            ref={feedbackFrameRef}
            id="feedbackFrame"
            src="https://feedbackform.readdy.co/feedback"
            width="100%"
            title="淡江大學春日拾光文創市集回饋表單"
            style={{ border: "none", display: "block", minHeight: "1000px" }}
          />
        </div>
      </section>
    </div>
  );
}